import { FC, useMemo } from "react";
import {
  ClaimStatus,
  ClaimEvent,
  LossEstimate,
  Claim,
  GetEditClaimOnFormResponseDocument,
} from "../../../../../../../graphql/__GENERATED__/types";
import { formatISODate } from "../../../../../../../utils/formatNumber";
import { concatArrayOfStrings } from "../../../../../../../utils/formatString";
import EditableSection from "../../../../../../../components/EditableSection";
import DetailsSectionColumns from "../../../../../../../components/Details/components/Main/Grid/DetailsSectionColumns";
import useDocumentDetails from "../../../../../../../hooks/useDocumentDetails";
import { useModalStore } from "../../../../../../../components/Modal/context/ModalStore";

export type MappedClaim = Claim & { eventsLabel: string | string[] };

interface ClaimProps {
  data: MappedClaim;
}

export const ClaimStatusLabels = {
  [ClaimStatus.Pending]: "Pendente",
  [ClaimStatus.Open]: "Aberto",
  [ClaimStatus.Approved]: "Aprovado",
  [ClaimStatus.Rejected]: "Recusado",
};

export const ClaimEventLabels = {
  [ClaimEvent.Fire]: "Incêndio",
  [ClaimEvent.Drought]: "Seca",
  [ClaimEvent.Rain]: "Chuva excessiva",
  [ClaimEvent.Lightning]: "Raio",
  [ClaimEvent.Hail]: "Granizo",
  [ClaimEvent.Wind]: "Vendaval",
  [ClaimEvent.Frost]: "Geada",
};

const LossEstimateLabels = {
  [LossEstimate.Low]: "Baixa",
  [LossEstimate.Medium]: "Média",
  [LossEstimate.High]: "Severa",
};

const ClaimComponent: FC<ClaimProps> = ({ data: claim }) => {
  const { refetch } = useDocumentDetails();
  const claimInfo = useMemo(() => {
    return [
      [
        {
          label: "ID na Seguradora",
          value: claim.externalId,
        },
        {
          label: "Status",
          value: ClaimStatusLabels[claim.status],
        },
        {
          label: "Data de criação",
          value: formatISODate(claim.createdAt),
        },
        {
          label: "Início do evento",
          value: formatISODate(claim.startDate),
        },
        {
          label: "Final do evento",
          value: formatISODate(claim.endDate),
        },
      ],
      [
        {
          label: "Eventos",
          value: claim.eventsLabel,
        },
        {
          label: "Perda estimada",
          value: LossEstimateLabels[claim.lossEstimate],
        },
        {
          label: "Área afetada pela ocorrência",
          value: claim.plots
            ? concatArrayOfStrings(claim.plots.map((plot) => `${plot.name}`))
            : "",
        },
        {
          label: "Observações",
          value: claim.comment,
        },
      ],
    ];
  }, [claim]);

  const { closeModal } = useModalStore();

  return (
    <EditableSection
      title={claim.name}
      editQuery={GetEditClaimOnFormResponseDocument}
      objectId={claim._id}
      main
      onSubmit={() => {
        refetch(), closeModal();
      }}
    >
      <DetailsSectionColumns columns={claimInfo} />
    </EditableSection>
  );
};

export default ClaimComponent;
